import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Grid, Card, Icon, Button, Message, Segment, Progress, Label } from 'semantic-ui-react';
import { useNavigate } from 'react-router-dom';
import { quizAttemptService, AvailableQuizDto } from '../../../services/learning/QuizAttemptService';
import { quizService } from '../../../services/learning/QuizService';
import { useAuth } from '../../../context/AuthContext';

interface QuizItem {
  id: string;
  title: string;
  description: string;
  category: string;
  difficulty: 'Básico' | 'Intermediário' | 'Avançado';
  questionsCount: number;
  estimatedTime: string;
  isAvailable: boolean;
  completionRate?: number;
  remainingAttempts?: number;
  unavailableReason?: string;
}

const QuizSection: React.FC = () => {
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const [availableQuizzes, setAvailableQuizzes] = useState<AvailableQuizDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [preflightingQuiz, setPreflightingQuiz] = useState<string | null>(null);
  const preflightCache = useRef<Map<string, number>>(new Map());
  const preflightTimeouts = useRef<Map<string, NodeJS.Timeout>>(new Map());

  useEffect(() => {
    const loadAvailableQuizzes = async () => {
      try {
        setLoading(true);
        const quizzes = await quizAttemptService.getAvailableQuizzes();
        setAvailableQuizzes(quizzes);
      } catch (err: any) {
        setError(err.message || 'Erro ao carregar questionários');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      loadAvailableQuizzes();
    }
  }, [user]);

  // Cleanup: limpar timeouts quando componente desmontar
  useEffect(() => {
    return () => {
      // Limpar todos os timeouts de preflight
      preflightTimeouts.current.forEach((timeout) => clearTimeout(timeout));
      preflightTimeouts.current.clear();
      
      // Limpar todos os timeouts de debounce
      debouncedPreflight.current.forEach((timeout) => clearTimeout(timeout));
      debouncedPreflight.current.clear();
    };
  }, []);

  // Preflight no hover: carregar quiz em background para warmup
  const preflightQuiz = useCallback(async (quizId: string) => {
    // Verificar cache (válido por 30 segundos)
    const cached = preflightCache.current.get(quizId);
    if (cached && Date.now() - cached < 30000) {
      return; // Já em cache, não precisa fazer preflight novamente
    }
    
    // Evitar preflight duplicado
    if (preflightingQuiz === quizId) {
      return;
    }
    
    setPreflightingQuiz(quizId);
    
    try {
      // Timeout de 5 segundos para não bloquear se demorar muito
      const timeoutPromise = new Promise((_, reject) => {
        const timeout = setTimeout(() => reject(new Error('Preflight timeout')), 5000);
        preflightTimeouts.current.set(quizId, timeout);
      });
      
      // Fazer preflight: carregar quiz em background
      await Promise.race([
        quizService.getQuiz(quizId),
        timeoutPromise
      ]);
      
      // Armazenar timestamp no cache
      preflightCache.current.set(quizId, Date.now());
      
      // Log silencioso para debugging (apenas em desenvolvimento)
      if (process.env.NODE_ENV === 'development') {
        console.debug(`[Preflight] Quiz ${quizId} preflighted successfully`);
      }
    } catch (error: any) {
      // Ignorar erros no preflight (não bloqueia UI)
      // Log silencioso para debugging
      if (process.env.NODE_ENV === 'development') {
        console.debug(`[Preflight] Error preflighting quiz ${quizId}:`, error.message);
      }
    } finally {
      // Limpar timeout se ainda existir
      const timeout = preflightTimeouts.current.get(quizId);
      if (timeout) {
        clearTimeout(timeout);
        preflightTimeouts.current.delete(quizId);
      }
      setPreflightingQuiz(null);
    }
  }, [preflightingQuiz]);

  // Debounce para preflight no hover (200ms)
  const debouncedPreflight = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const handleMouseEnter = useCallback((quizId: string) => {
    // Limpar timeout anterior se existir
    const existingTimeout = debouncedPreflight.current.get(quizId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }
    
    // Criar novo timeout com debounce de 200ms
    const timeout = setTimeout(() => {
      preflightQuiz(quizId);
      debouncedPreflight.current.delete(quizId);
    }, 200);
    
    debouncedPreflight.current.set(quizId, timeout);
  }, [preflightQuiz]);

  const handleStartQuiz = async (quizId: string) => {
    try {
      // Preflight no clique: garantir que dados estão carregados antes de navegar
      const [quizData, currentAvailability] = await Promise.all([
        quizService.getQuiz(quizId).catch((err) => {
          // Se falhar, tentar novamente uma vez (retry limitado)
          return quizService.getQuiz(quizId);
        }),
        Promise.resolve(availableQuizzes.find(q => q.id === quizId))
      ]);
      
      // Passar dados via state para evitar requisição redundante
      navigate(`/learning/quizzes/${quizId}/take`, {
        state: { 
          preloadedQuiz: quizData,
          preloadedAvailability: currentAvailability
        }
      });
    } catch (err: any) {
      setError(err.message || 'Erro ao iniciar questionário');
    }
  };

  const getCategoryColor = (category?: string) => {
    const colors: { [key: string]: string } = {
      'Introdução': 'blue',
      'Contratos': 'green',
      'Clientes': 'purple',
      'WhatsApp': 'teal',
      'Relatórios': 'red',
      'Boas Práticas': 'yellow',
      'Administração': 'orange'
    };
    return colors[category || ''] || 'blue';
  };

  if (loading) {
    return (
      <Message info>
        <Icon name="spinner" loading />
        Carregando questionários disponíveis...
      </Message>
    );
  }

  if (error) {
    return (
      <Message negative>
        <Message.Header>Erro</Message.Header>
        <p>{error}</p>
      </Message>
    );
  }

  if (availableQuizzes.length === 0) {
    return (
      <Message info>
        <Message.Header>
          <Icon name="info circle" />
          Nenhum questionário disponível
        </Message.Header>
        <p>
          Não há questionários disponíveis no momento. Entre em contato com o administrador para mais informações.
        </p>
      </Message>
    );
  }

  const quizItems: QuizItem[] = availableQuizzes.map(quiz => ({
    id: quiz.id,
    title: quiz.title,
    description: quiz.description || 'Questionário de avaliação',
    category: quiz.category || 'Geral',
    difficulty: 'Intermediário' as const,
    questionsCount: quiz.questionsCount,
    estimatedTime: quiz.timeLimitMinutes ? `${quiz.timeLimitMinutes} min` : 'Sem limite',
    isAvailable: quiz.isAvailable,
    completionRate: 0,
    remainingAttempts: quiz.remainingAttempts,
    unavailableReason: quiz.unavailableReason,
  }));


  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
        <Message info style={{ flex: 1, marginRight: hasRole('Admin') ? '20px' : '0' }}>
          <Message.Header>
            <Icon name="question circle" />
            Questionários Interativos
          </Message.Header>
          <p>
            Teste seus conhecimentos através de questionários interativos sobre diferentes módulos do sistema.
            Os questionários ajudarão a reforçar o aprendizado e identificar áreas que precisam de mais atenção.
          </p>
        </Message>
        {hasRole('Admin') && (
          <Button
            primary
            icon="settings"
            content="Gerenciar Questionários"
            onClick={() => navigate('/learning/quizzes/manage')}
            style={{ whiteSpace: 'nowrap', alignSelf: 'flex-start' }}
          />
        )}
      </div>

      <Grid columns={2} stackable>
        {quizItems.map((quiz) => (
          <Grid.Column key={quiz.id}>
            <Card
              fluid
              style={{
                cursor: quiz.isAvailable ? 'pointer' : 'not-allowed',
                opacity: quiz.isAvailable ? 1 : 0.7,
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => {
                if (quiz.isAvailable) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                }
              }}
              onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
              }}
            >
              <Card.Content>
                <Card.Header style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Icon name="question circle" color={getCategoryColor(quiz.category) as any} />
                  {quiz.title}
                </Card.Header>

                <Card.Meta style={{ marginTop: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <Label color={getCategoryColor(quiz.category) as any} size="tiny">
                      {quiz.category}
                    </Label>
                    <span style={{ color: '#666', fontSize: '12px' }}>
                      <Icon name="clock" />
                      {quiz.estimatedTime}
                    </span>
                    <span style={{ color: '#666', fontSize: '12px' }}>
                      <Icon name="list" />
                      {quiz.questionsCount} questões
                    </span>
                  </div>
                </Card.Meta>

                <Card.Description style={{ marginTop: '12px' }}>
                  {quiz.description}
                </Card.Description>

                {quiz.completionRate !== undefined && quiz.completionRate > 0 && (
                  <div style={{ marginTop: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <small style={{ color: '#666' }}>Progresso</small>
                      <small style={{ color: '#666' }}>{quiz.completionRate}%</small>
                    </div>
                    <Progress
                      percent={quiz.completionRate}
                      size="tiny"
                      color="blue"
                      style={{ margin: 0 }}
                    />
                  </div>
                )}
              </Card.Content>

              <Card.Content extra>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    {quiz.isAvailable ? (
                      <Label color="green" size="small">
                        <Icon name="check" />
                        Disponível
                        {typeof quiz.remainingAttempts === 'number' && (
                          <span style={{ marginLeft: 6 }}>
                            ({quiz.remainingAttempts} tentativa{quiz.remainingAttempts === 1 ? '' : 's'} restante{quiz.remainingAttempts === 1 ? '' : 's'})
                          </span>
                        )}
                      </Label>
                    ) : (
                      <Label color="grey" size="small">
                        <Icon name="lock" />
                        {quiz.remainingAttempts !== undefined && quiz.remainingAttempts <= 0
                          ? 'Sem tentativas restantes'
                          : 'Indisponível'}
                      </Label>
                    )}
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <Button
                      primary={quiz.isAvailable}
                      disabled={!quiz.isAvailable}
                      size="small"
                      onMouseEnter={() => quiz.isAvailable && handleMouseEnter(quiz.id)}
                      onClick={() => quiz.isAvailable && handleStartQuiz(quiz.id)}
                    >
                      <Icon name={quiz.isAvailable ? 'play' : 'lock'} />
                      {quiz.isAvailable ? 'Iniciar' : 'Bloqueado'}
                    </Button>
                    {!quiz.isAvailable && quiz.unavailableReason && (
                      <div style={{ marginTop: 4, fontSize: 11, color: '#888', maxWidth: 220, textAlign: 'right' }}>
                        {quiz.unavailableReason}
                      </div>
                    )}
                  </div>
                </div>
              </Card.Content>
            </Card>
          </Grid.Column>
        ))}
      </Grid>

      <Segment style={{ marginTop: '30px' }}>
        <h4 style={{ marginBottom: '16px', color: '#2c3e50' }}>
          <Icon name="trophy" />
          Sistema de Certificação
        </h4>
        <p style={{ color: '#666', marginBottom: '16px' }}>
          Ao completar os questionários com sucesso, você poderá obter certificações que demonstram seu domínio
          sobre diferentes módulos do sistema Gettel. As certificações ficarão registradas em seu perfil.
        </p>

        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <Label color="blue" size="large">
            <Icon name="certificate" />
            Certificado Básico
          </Label>
          <Label color="yellow" size="large">
            <Icon name="star" />
            Certificado Intermediário
          </Label>
          <Label color="red" size="large">
            <Icon name="trophy" />
            Certificado Avançado
          </Label>
        </div>
      </Segment>
    </div>
  );
};

export default QuizSection;
